import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, Button, Row, Col, Image, Tabs, Tab, Card, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSpinner, faUpload, faFileImage, faPlus, faPen, faTrash, faStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarOutline } from '@fortawesome/free-regular-svg-icons';

import {
  useCreateProvider,
  useUpdateProvider,
  useUploadProviderSignature,
  useUploadProviderLetterhead,
  useFetchProviderProfile,
  useCreateProviderProfile,
  useUpdateProviderProfile,
  useDeleteProviderProfile,
  useSetDefaultProviderProfile,
} from '../../../services/users';
import { useForm } from '../../../hooks/useForm';
import ClaimSelect from '../../common/ClaimSelect';
import TimezoneSelect from '../../common/TimezoneSelect';
import StateSelect from '../../common/StateSelect';
import SignatureInput from '../../common/SignatureInput';
import AddressInput from '../../common/AddressInput';
import {
  PROVIDER_GROUPS,
  groupSupportsNotifications,
} from '../../../lib/providerGroups';

const DEFAULTS = {
  userEmail: '',
  userFirstName: '',
  userLastName: '',
  userType: 'provider',
  userDateOfBirth: '1950-01-01',
  userTimezone: 'America/Chicago',
  userState: '',
  userPhone: '',
  claims: [],
  providerGroup: '',
  providerGroupNotifyEmails: '',
};

const PROFILE_DEFAULTS = {
  profileName: '',
  title: '',
  specialty: '',
  npi: '',
  licenseNumber: '',
  licenseState: '',
  address: '',
  businessPhone: '',
  isDefault: false,
};

const MAX_PROFILES = 3;

const ProviderModal = ({ show, toggle, user, onSuccess }) => {
  const { form, hasChanged, onChange, setValues, resetChange } = useForm(DEFAULTS);
  const [activeTab, setActiveTab] = useState('basic');
  const [letterheadFile, setLetterheadFile] = useState(null);
  const [letterheadPreview, setLetterheadPreview] = useState(null);
  const letterheadInputRef = useRef(null);
  // Pending signature captured from SignatureInput; uploaded by the footer Save
  // (like the letterhead), so there's a single Save for the whole modal.
  const [signatureFile, setSignatureFile] = useState(null);

  // Profile state
  const [profiles, setProfiles] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState(PROFILE_DEFAULTS);
  const [profileToDelete, setProfileToDelete] = useState(null);

  // Fetch provider profile with presigned signature URL when editing. Keyed off
  // user?.userId OR form.userId so it also loads for a provider we just created
  // in-modal (create-then-stay-open flow), not only when opened in edit mode.
  const { data: profileData, isLoading: isLoadingProfile, refetch: refetchProfile } = useFetchProviderProfile(
    show ? (user?.userId || form.userId || null) : null
  );

  // Set form values from user prop
  useEffect(() => {
    if (user) {
      const claims = user._claims || [];

      setValues({
        ...DEFAULTS,
        ...user,
        claims: claims.map((c) => {
          return {
            label: `${c.claimUserFirstName} ${c.claimUserLastName}`,
            value: c.claimId,
          };
        }),
      });
      setLetterheadPreview(null);
    } else {
      setValues(DEFAULTS);
      setLetterheadPreview(null);
    }
    setLetterheadFile(null);
    setSignatureFile(null);
    setProfiles([]);
    setActiveTab('basic');
    // setValues above flips the form's dirty flag; clear it so the unsaved-changes
    // guard only trips on actual user edits, not this programmatic load.
    resetChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Set letterhead preview and profiles from profile data
  useEffect(() => {
    if (profileData?.provider) {
      if (profileData.provider.providerLetterheadUrl) {
        setLetterheadPreview(profileData.provider.providerLetterheadUrl);
      }
      setProfiles(profileData.provider.profiles || []);
      // Group fields come from the profile read, not the list payload, so
      // hydrate them here. setValues always flips the dirty flag, hence the
      // resetChange: this is a programmatic load, not a user edit, and the
      // unsaved-changes guard should not trip just from opening the modal.
      setValues(
        {
          providerGroup: profileData.provider.providerGroup || '',
          providerGroupNotifyEmails:
            profileData.provider.providerGroupNotifyEmails || '',
        },
        true,
      );
      resetChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  const sendInvite = useCreateProvider(
    (data) => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }

      // Keep the modal open and flip it into edit mode so the admin can add
      // certification info, a signature, and a letterhead right away — instead
      // of saving, hunting for the new provider in the list, and re-opening it.
      if (data?.userId) {
        setValues({ userId: data.userId }, true);
        resetChange(); // just saved — start clean so closing won't prompt
        // Upload any signature/letterhead the admin staged before the first Save,
        // now that the provider (and its userId) exists.
        if (letterheadFile) {
          uploadLetterhead.mutate({ userId: data.userId, file: letterheadFile });
        }
        if (signatureFile) {
          uploadSignature.mutate({ userId: data.userId, file: signatureFile });
        }
        toast.success(
          'Provider created and invite sent. You can now add certification info, a signature, and a letterhead.',
        );
      } else {
        // Anomalous: created but no userId returned — nothing more to do, close
        // cleanly (create succeeded, so no unsaved-changes prompt needed).
        toast.success('Provider created and invite sent successfully.');
        toggle();
      }
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.message); } },
  );

  const updateUser = useUpdateProvider(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      resetChange(); // saved — keep the modal open, close via the ✕
      // Upload any staged signature/letterhead next; each toasts on its own.
      const uploadingLetterhead = !!(letterheadFile && form.userId);
      const uploadingSignature = !!(signatureFile && form.userId);
      if (uploadingLetterhead) {
        uploadLetterhead.mutate({ userId: form.userId, file: letterheadFile });
      }
      if (uploadingSignature) {
        uploadSignature.mutate({ userId: form.userId, file: signatureFile });
      }
      if (!uploadingLetterhead && !uploadingSignature) {
        toast.success('Provider successfully updated.');
      }
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.message); } },
  );

  const uploadSignature = useUploadProviderSignature(
    () => {
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      setSignatureFile(null); // uploaded — clear so a later Save won't re-upload
      resetChange();
      toast.success('Signature saved successfully.');
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.message); } },
  );

  const uploadLetterhead = useUploadProviderLetterhead(
    (data) => {
      // Use the presigned URL returned from upload for immediate preview
      if (data?.letterheadUrl) {
        setLetterheadPreview(data.letterheadUrl);
      }
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      setLetterheadFile(null); // uploaded — clear so a later Save won't re-upload
      resetChange(); // saved — keep the modal open, close via the ✕
      toast.success('Provider updated successfully.');
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.message); } },
  );

  // Profile mutations
  const createProfile = useCreateProviderProfile(
    () => {
      toast.success('Profile created successfully.');
      setShowProfileModal(false);
      refetchProfile();
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.response?.data?.msg || 'Failed to create profile'); } },
  );

  const updateProfile = useUpdateProviderProfile(
    () => {
      toast.success('Profile updated successfully.');
      setShowProfileModal(false);
      refetchProfile();
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.response?.data?.msg || 'Failed to update profile'); } },
  );

  const deleteProfile = useDeleteProviderProfile(
    () => {
      toast.success('Profile deleted successfully.');
      setProfileToDelete(null);
      refetchProfile();
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.response?.data?.msg || 'Failed to delete profile'); } },
  );

  const setDefaultProfile = useSetDefaultProviderProfile(
    () => {
      toast.success('Default profile updated.');
      refetchProfile();
    },
    { onError: (err) => { if (err.response?.status !== 403) toast.error(err.response?.data?.msg || 'Failed to set default profile'); } },
  );

  const handleLetterheadChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLetterheadFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLetterheadPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Profile modal handlers
  const onProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const openAddProfile = () => {
    setEditingProfile(null);
    setProfileForm(PROFILE_DEFAULTS);
    setShowProfileModal(true);
  };

  const openEditProfile = (profile) => {
    setEditingProfile(profile);
    setProfileForm({
      profileName: profile.profileName || '',
      title: profile.title || '',
      specialty: profile.specialty || '',
      npi: profile.npi || '',
      licenseNumber: profile.licenseNumber || '',
      licenseState: profile.licenseState || '',
      address: profile.address || '',
      businessPhone: profile.businessPhone || '',
      isDefault: profile.isDefault || false,
    });
    setShowProfileModal(true);
  };

  const handleProfileSubmit = (e) => {
    e.preventDefault();

    const payload = {
      profileName: profileForm.profileName,
      title: profileForm.title || null,
      specialty: profileForm.specialty || null,
      npi: profileForm.npi || null,
      licenseNumber: profileForm.licenseNumber,
      licenseState: profileForm.licenseState,
      address: profileForm.address || null,
      businessPhone: profileForm.businessPhone || null,
      isDefault: profileForm.isDefault,
    };

    if (editingProfile) {
      updateProfile.mutate({ userId: form.userId, profileId: editingProfile.profileId, payload });
    } else {
      createProfile.mutate({ userId: form.userId, payload });
    }
  };

  const handleDeleteProfile = () => {
    if (profileToDelete) {
      deleteProfile.mutate({ userId: form.userId, profileId: profileToDelete.profileId });
    }
  };

  const handleSetDefault = (profileId) => {
    setDefaultProfile.mutate({ userId: form.userId, profileId });
  };

  const onSubmit = (e) => {
    e.preventDefault();

    // When creating, make sure the required basics are filled across tabs
    // before saving (they live on the Basic Info tab). Guarding here avoids the
    // browser's "invalid form control is not focusable" failure if Save is
    // clicked from another tab while a required Basic field is empty.
    if (!form.userId) {
      const missingBasics =
        !form.userEmail?.trim() ||
        !form.userFirstName?.trim() ||
        !form.userLastName?.trim();
      if (missingBasics) {
        setActiveTab('basic');
        toast.error('Please fill in email, first name, and last name.');
        return;
      }
    }

    // Address must be picked from the suggestions (latitude/longitude are only
    // stamped on selection) so the provider has a real point on the Coverage Map.
    const typedButNotVerified =
      (form.addressLine1 || '').trim().length > 0 &&
      (form.latitude == null || form.longitude == null);
    if (typedButNotVerified) {
      setActiveTab('basic');
      toast.error(
        'Please pick the practice address from the suggestions to verify it.',
      );
      return;
    }

    const payload = {
      userEmail: form.userEmail,
      userFirstName: form.userFirstName,
      userLastName: form.userLastName,
      userType: form.userType,
      userDateOfBirth: form.userDateOfBirth,
      userTimezone: form.userTimezone,
      // Keep userState in sync with the structured address state when set, so
      // legacy callers reading userState don't drift.
      userState: form.addressState || form.userState || null,
      userPhone: form.userPhone || null,
      claims: form.claims.map((v) => v.value),
      addressLine1: form.addressLine1 || null,
      addressLine2: form.addressLine2 || null,
      addressCity: form.addressCity || null,
      addressState: form.addressState || null,
      addressZip: form.addressZip || null,
      addressCountry: form.addressCountry || 'US',
      latitude: form.latitude ?? null,
      longitude: form.longitude ?? null,
      providerCoverageMileRadius:
        Number(form.providerCoverageMileRadius) > 0
          ? Number(form.providerCoverageMileRadius)
          : 50,
      // Group tab. This payload is an explicit allow-list, not a spread of
      // form, so anything not named here is silently dropped on save.
      providerGroup: form.providerGroup || null,
      // Cleared whenever the group does not support notifications, so switching
      // FasPsych -> Organic cannot leave a stale address behind that would
      // start sending again if the group were ever switched back.
      providerGroupNotifyEmails: groupSupportsNotifications(form.providerGroup)
        ? form.providerGroupNotifyEmails || null
        : null,
    };

    if (form.userId) {
      updateUser.mutate({
        userId: form.userId,
        payload,
      });
    } else {
      sendInvite.mutate(payload);
    }
  };

  // Close handler for the ✕ / ESC / backdrop. Save never auto-closes anymore, so
  // guard against losing unsaved Basic Info edits, a picked-but-not-yet-saved logo
  // (letterheadFile), or a typed/drawn-but-not-yet-saved signature (signatureFile).
  // hasChanged is a coarse "touched" flag (it doesn't reset if a field is edited
  // back to its original value), hence the softer "You may have unsaved changes"
  // wording. Cert profiles save on their own buttons, so they're never "unsaved".
  const handleClose = () => {
    // A staged asset that's currently uploading isn't "unsaved" — it's saving —
    // so don't prompt during that ~1-2s window (it clears on the upload's success;
    // on failure isLoading flips back to false and it counts as unsaved again).
    const pendingLetterhead = letterheadFile && !uploadLetterhead.isLoading;
    const pendingSignature = signatureFile && !uploadSignature.isLoading;
    if (hasChanged || pendingLetterhead || pendingSignature) {
      if (!window.confirm('You may have unsaved changes. Close anyway?')) {
        return;
      }
    }
    toggle();
  };

  const isLoading = sendInvite.isLoading || updateUser.isLoading || uploadSignature.isLoading || uploadLetterhead.isLoading;
  const isProfileLoading = createProfile.isLoading || updateProfile.isLoading || deleteProfile.isLoading || setDefaultProfile.isLoading;
  const isEditing = !!form.userId;

  // Provider address rule mirrors the client modals: empty OR verified,
  // never partial. An empty address is fine (the provider just won't show
  // on the Coverage Map until verified), but any address actually entered
  // must be verified to save.
  const providerLine1 = (form.addressLine1 || '').trim();
  const providerCity = (form.addressCity || '').trim();
  const providerStateField = (form.addressState || '').trim();
  const providerZip = (form.addressZip || '').trim();
  const providerAddressEmpty =
    !providerLine1 &&
    !providerCity &&
    !providerStateField &&
    !providerZip &&
    form.latitude == null &&
    form.longitude == null;
  const providerAddressVerified =
    !!providerLine1 &&
    !!providerCity &&
    !!providerStateField &&
    !!providerZip &&
    form.latitude != null &&
    form.longitude != null;
  const providerAddressOk = providerAddressEmpty || providerAddressVerified;

  return (
    <>
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Provider' : 'Create Provider'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id="invite-form" onSubmit={onSubmit}>
          <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
            <Tab eventKey="basic" title="Basic Info">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="userEmail"
                      onChange={onChange}
                      value={form.userEmail}
                      required
                      placeholder="user@email.com"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="userPhone"
                      onChange={onChange}
                      value={form.userPhone || ''}
                      placeholder="(555) 123-4567"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      name="userFirstName"
                      onChange={onChange}
                      value={form.userFirstName}
                      required
                      placeholder="First"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      name="userLastName"
                      onChange={onChange}
                      value={form.userLastName}
                      required
                      placeholder="Last"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Timezone</Form.Label>
                    <TimezoneSelect
                      name="userTimezone"
                      onChange={onChange}
                      value={form.userTimezone}
                      require={true}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={8}>
                  <AddressInput
                    value={{
                      line1: form.addressLine1 || '',
                      line2: form.addressLine2 || '',
                      city: form.addressCity || '',
                      state: form.addressState || '',
                      zip: form.addressZip || '',
                      country: form.addressCountry || 'US',
                      latitude: form.latitude ?? null,
                      longitude: form.longitude ?? null,
                    }}
                    onChange={(next) =>
                      setValues({
                        ...form,
                        addressLine1: next.line1 || '',
                        addressLine2: next.line2 || '',
                        addressCity: next.city || '',
                        addressState: next.state || '',
                        addressZip: next.zip || '',
                        addressCountry: next.country || 'US',
                        latitude: next.latitude ?? null,
                        longitude: next.longitude ?? null,
                      })
                    }
                    requireVerified
                    label="Practice / Business Address"
                    placeholder="Street address of the practice..."
                    idPrefix="provider-address"
                  />
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Coverage Radius (miles)</Form.Label>
                    <Form.Control
                      type="number"
                      name="providerCoverageMileRadius"
                      min={1}
                      max={500}
                      value={form.providerCoverageMileRadius ?? 50}
                      onChange={onChange}
                    />
                    <Form.Text className="text-muted">
                      How far this provider will see clients on the Coverage
                      Map.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Claim Access</Form.Label>
                <ClaimSelect
                  value={form.claims}
                  onChange={(vals) =>
                    onChange({ target: { name: 'claims', value: vals } })
                  }
                  isMulti={true}
                />
              </Form.Group>
            </Tab>

            <Tab eventKey="certification" title="Certification Info">
              <p className="text-muted small mb-3">
                Manage certification profiles for DBQ form signing. Up to {MAX_PROFILES} profiles allowed.
              </p>

              {!isEditing ? (
                <p className="text-muted">Save the provider first to manage certification profiles.</p>
              ) : (
                <Row>
                  {/* Render existing profiles */}
                  {profiles.map((profile) => (
                    <Col md={4} key={profile.profileId} className="mb-3">
                      <Card className={`h-100 ${profile.isDefault ? 'border-primary' : ''}`}>
                        <Card.Body className="d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-0">{profile.profileName}</h6>
                            {profile.isDefault && (
                              <Badge bg="primary">
                                <FontAwesomeIcon icon={faStar} className="me-1" />
                                Default
                              </Badge>
                            )}
                          </div>
                          <div className="text-muted small flex-grow-1">
                            {profile.title && <div>{profile.title}</div>}
                            {profile.specialty && <div>{profile.specialty}</div>}
                            {profile.npi && <div>NPI: {profile.npi}</div>}
                            <div className="mt-1">
                              License: {profile.licenseNumber} ({profile.licenseState})
                            </div>
                            {profile.businessPhone && <div>{profile.businessPhone}</div>}
                            {profile.address && <div className="mt-1">{profile.address}</div>}
                          </div>
                          <div className="d-flex gap-2 mt-3 pt-2 border-top">
                            {!profile.isDefault && (
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleSetDefault(profile.profileId)}
                                disabled={isProfileLoading}
                                title="Set as default"
                              >
                                <FontAwesomeIcon icon={faStarOutline} />
                              </Button>
                            )}
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => openEditProfile(profile)}
                              disabled={isProfileLoading}
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => setProfileToDelete(profile)}
                              disabled={isProfileLoading}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}

                  {/* Render empty slots with + button */}
                  {Array.from({ length: MAX_PROFILES - profiles.length }).map((_, index) => (
                    <Col md={4} key={`empty-${index}`} className="mb-3">
                      <Card
                        className="h-100"
                        style={{ borderStyle: 'dashed', cursor: 'pointer', minHeight: '180px' }}
                        onClick={openAddProfile}
                      >
                        <Card.Body className="d-flex flex-column align-items-center justify-content-center text-muted">
                          <FontAwesomeIcon icon={faPlus} size="2x" className="mb-2" />
                          <span>Add Profile</span>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Tab>

            <Tab eventKey="signature" title="Signature">
              <p className="text-muted small mb-3">
                Draw, type, or upload the provider's signature for DBQ form signing.
                It saves when you click Save below.
              </p>

              <SignatureInput
                onChange={setSignatureFile}
                showSaveButton={false}
                currentSignatureUrl={profileData?.provider?.providerSignatureUrl}
                disabled={isLoading}
                isLoading={isLoadingProfile}
                drawModeReadOnly={true}
              />
            </Tab>

            <Tab eventKey="letterhead" title="Letterhead">
              <p className="text-muted small mb-3">
                Upload your logo and preview how your letterhead will appear on generated PDFs.
              </p>

              {/* Logo Upload Section */}
              <Form.Group className="mb-4">
                <Form.Label>Provider Logo</Form.Label>
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="border rounded p-3 text-center"
                    style={{
                      minWidth: '150px',
                      minHeight: '80px',
                      background: letterheadPreview ? '#fff' : '#f8f9fa',
                      cursor: 'pointer'
                    }}
                    onClick={() => letterheadInputRef.current?.click()}
                  >
                    {isLoadingProfile && !letterheadFile ? (
                      <div className="text-muted py-2">
                        <FontAwesomeIcon icon={faSpinner} spin size="lg" className="mb-1" />
                        <br />
                        <small>Loading...</small>
                      </div>
                    ) : letterheadPreview ? (
                      <Image
                        src={letterheadPreview}
                        alt="Logo Preview"
                        style={{ maxWidth: '120px', maxHeight: '80px' }}
                      />
                    ) : (
                      <div className="text-muted py-2">
                        <FontAwesomeIcon icon={faFileImage} size="lg" className="mb-1" />
                        <br />
                        <small>Click to upload</small>
                      </div>
                    )}
                  </div>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => letterheadInputRef.current?.click()}
                    >
                      <FontAwesomeIcon icon={faUpload} className="me-1" />
                      {letterheadPreview ? 'Change Logo' : 'Upload Logo'}
                    </Button>
                    <Form.Text className="d-block text-muted mt-2">
                      PNG, JPEG, GIF, WebP (max 5MB)
                    </Form.Text>
                  </div>
                </div>
                <input
                  type="file"
                  ref={letterheadInputRef}
                  onChange={handleLetterheadChange}
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  style={{ display: 'none' }}
                />
              </Form.Group>

              {/* Letterhead Preview */}
              <Form.Label>Letterhead Preview</Form.Label>
              <div
                className="border rounded p-4 mb-3"
                style={{
                  background: '#fff',
                  minHeight: '120px'
                }}
              >
                <div className="d-flex justify-content-between align-items-start">
                  {/* Left side - Logo */}
                  <div style={{ minWidth: '150px' }}>
                    {letterheadPreview ? (
                      <Image
                        src={letterheadPreview}
                        alt="Logo"
                        style={{ maxWidth: '150px', maxHeight: '100px' }}
                      />
                    ) : (
                      <div
                        className="border rounded d-flex align-items-center justify-content-center text-muted"
                        style={{ width: '120px', height: '60px', background: '#f8f9fa', fontSize: '0.75rem' }}
                      >
                        Logo
                      </div>
                    )}
                  </div>

                  {/* Right side - Provider Info (uses default profile if available) */}
                  <div className="text-end" style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                    {(() => {
                      const defaultProfile = profiles.find(p => p.isDefault) || profiles[0];
                      return (
                        <>
                          <div style={{ fontWeight: '600' }}>
                            {form.userFirstName || form.userLastName
                              ? `${form.userFirstName} ${form.userLastName}`.trim()
                              : 'Examiner Name'}
                            {defaultProfile?.title && `, ${defaultProfile.title}`}
                          </div>
                          <div className="text-muted">
                            {defaultProfile?.businessPhone || 'Phone Number'}
                          </div>
                          <div className="text-muted">
                            {form.userEmail || 'email@example.com'}
                          </div>
                          {defaultProfile?.address ? (
                            defaultProfile.address.split('\n').map((line, idx) => (
                              <div key={idx} className="text-muted">{line}</div>
                            ))
                          ) : (
                            <>
                              <div className="text-muted">Address Line 1</div>
                              <div className="text-muted">City, State ZIP</div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <Form.Text className="text-muted">
                This preview shows how your letterhead will appear on generated PDFs.
                Update the Certification Info tab to change the contact details.
              </Form.Text>
            </Tab>

            <Tab eventKey="group" title="Group">
              <p className="text-muted small mb-3">
                The organization this provider works under. Used to route
                completion notifications; it does not affect DBQ assignment.
              </p>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Provider Group</Form.Label>
                    <Form.Select
                      name="providerGroup"
                      onChange={onChange}
                      value={form.providerGroup || ''}
                    >
                      <option value="">Not set</option>
                      {PROVIDER_GROUPS.map((group) => (
                        <option key={group} value={group}>
                          {group}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {groupSupportsNotifications(form.providerGroup) && (
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Notification Emails</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="providerGroupNotifyEmails"
                        onChange={onChange}
                        value={form.providerGroupNotifyEmails || ''}
                        placeholder="scheduling@example.com, coordinator@example.com"
                      />
                      <Form.Text className="text-muted">
                        Separate multiple addresses with commas. These are
                        emailed once when every DBQ assigned to this provider
                        on a claim reaches Provider Complete. Leave blank to
                        send nothing.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </Tab>
          </Tabs>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          variant="success"
          form="invite-form"
          disabled={isLoading || !providerAddressOk}
          title={
            !providerAddressOk
              ? 'Finish the address (pick a verified suggestion) or clear it entirely to enable Save.'
              : undefined
          }
        >
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : faCheck}
            spin={isLoading}
          />{' '}
          Save
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Profile Add/Edit Modal */}
    <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{editingProfile ? 'Edit Profile' : 'Add Certification Profile'}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleProfileSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Profile Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              name="profileName"
              onChange={onProfileChange}
              value={profileForm.profileName}
              placeholder="e.g., Texas License, California Practice"
              required
            />
            <Form.Text className="text-muted">
              A name to identify this profile (e.g., state name or practice location)
            </Form.Text>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Title / Credentials</Form.Label>
                <Form.Control
                  name="title"
                  onChange={onProfileChange}
                  value={profileForm.title}
                  placeholder="e.g., MD, DO, NP, PA-C, Ph.D"
                />
                <Form.Text className="text-muted">
                  Professional designation
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Area of Practice / Specialty</Form.Label>
                <Form.Control
                  name="specialty"
                  onChange={onProfileChange}
                  value={profileForm.specialty}
                  placeholder="e.g., Psychiatry, Orthopedics"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>NPI Number</Form.Label>
                <Form.Control
                  name="npi"
                  onChange={onProfileChange}
                  value={profileForm.npi}
                  placeholder="National Provider Identifier"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Business Phone / Fax</Form.Label>
                <Form.Control
                  type="tel"
                  name="businessPhone"
                  onChange={onProfileChange}
                  value={profileForm.businessPhone}
                  placeholder="(555) 123-4567"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Medical License Number <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  name="licenseNumber"
                  onChange={onProfileChange}
                  value={profileForm.licenseNumber}
                  placeholder="License #"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>License State <span className="text-danger">*</span></Form.Label>
                <StateSelect
                  name="licenseState"
                  onChange={onProfileChange}
                  value={profileForm.licenseState}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Business Address</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="address"
              onChange={onProfileChange}
              value={profileForm.address}
              placeholder="Full business/practice address"
            />
          </Form.Group>

          {profiles.length > 0 && (
            <Form.Check
              type="checkbox"
              id="profile-default"
              name="isDefault"
              label="Set as default profile"
              checked={profileForm.isDefault}
              onChange={(e) => onProfileChange({ target: { name: 'isDefault', value: e.target.checked } })}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowProfileModal(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={createProfile.isLoading || updateProfile.isLoading}
          >
            <FontAwesomeIcon
              icon={(createProfile.isLoading || updateProfile.isLoading) ? faSpinner : faCheck}
              spin={createProfile.isLoading || updateProfile.isLoading}
              className="me-2"
            />
            {editingProfile ? 'Save Changes' : 'Create Profile'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>

    {/* Delete Confirmation Modal */}
    <Modal show={!!profileToDelete} onHide={() => setProfileToDelete(null)}>
      <Modal.Header closeButton>
        <Modal.Title>Delete Profile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Are you sure you want to delete <strong>{profileToDelete?.profileName}</strong>?
        {profileToDelete?.isDefault && profiles.length > 1 && (
          <div className="alert alert-warning mt-3 mb-0">
            This is the default profile. Another profile will be automatically set as default.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setProfileToDelete(null)}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleDeleteProfile}
          disabled={deleteProfile.isLoading}
        >
          <FontAwesomeIcon
            icon={deleteProfile.isLoading ? faSpinner : faTrash}
            spin={deleteProfile.isLoading}
            className="me-2"
          />
          Delete Profile
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default ProviderModal;
